import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Star,
  Clock,
  ShieldCheck,
  Sparkles,
  ChevronRight,
  UtensilsCrossed,
  Users,
  Award,
  MapPin,
  Quote,
} from "lucide-react";
import { ChefHat } from "@/components/svg/ChefHat";

export const Route = createFileRoute("/(public)/preview")({
  component: PreviewPage,
});

// ─── Static Data ─────────────────────────────────────────────────────────────

const chefs = [
  {
    name: "Sophie Laurent",
    specialty: "Cuisine française gastronomique",
    location: "Paris 8ème",
    rating: 4.9,
    tag: "Étoilée Michelin",
    cardClass: "bg-salmon border border-black/5",
    light: false,
  },
  {
    name: "Marc Dubois",
    specialty: "Cuisine méditerranéenne",
    location: "Lyon",
    rating: 5.0,
    tag: "Chef du Mois",
    cardClass: "bg-dark-blue border border-white/5",
    light: true,
  },
  {
    name: "Antoine Moreau",
    specialty: "Fusion franco-asiatique",
    location: "Bordeaux",
    rating: 4.8,
    tag: "Coup de Cœur",
    cardClass: "bg-white border border-border",
    light: false,
  },
  {
    name: "Camille Bernard",
    specialty: "Pâtisserie & desserts fins",
    location: "Marseille",
    rating: 4.9,
    tag: "Spécialiste desserts",
    cardClass: "bg-primary border border-primary",
    light: true,
  },
];

const reviews = [
  {
    author: "Marie T.",
    event: "Dîner d'anniversaire · 12 personnes",
    text: "Une soirée absolument magique. Sophie a transformé notre dîner en une expérience inoubliable. Chaque plat était une œuvre d'art.",
  },
  {
    author: "Jean-Pierre M.",
    event: "Repas de famille · 20 personnes",
    text: "Marc a su s'adapter à tous nos régimes alimentaires sans compromis sur le goût. Nos invités en parlent encore deux semaines après.",
  },
  {
    author: "Isabelle R.",
    event: "Soirée d'entreprise · 30 personnes",
    text: "Service impeccable de A à Z. Antoine est arrivé, a tout géré, et nous a laissé profiter pleinement de nos invités. Parfait.",
  },
];

const steps = [
  {
    num: "01",
    title: "Décrivez votre occasion",
    desc: "Remplissez notre formulaire en 2 minutes. Date, convives, budget, envies culinaires — on s'occupe du reste.",
    icon: <Sparkles size={20} />,
  },
  {
    num: "02",
    title: "On trouve votre chef",
    desc: "Notre équipe sélectionne le chef parfait parmi +150 profils et vous envoie une proposition personnalisée.",
    icon: <Users size={20} />,
  },
  {
    num: "03",
    title: "Profitez, c'est tout",
    desc: "Le chef se déplace, s'occupe de tout — courses, préparation, service et nettoyage inclus.",
    icon: <UtensilsCrossed size={20} />,
  },
];

const stats = [
  { value: "150+", label: "Chefs qualifiés", icon: <ChefHat size={22} /> },
  { value: "500+", label: "Événements réalisés", icon: <Award size={22} /> },
  { value: "4.9/5", label: "Note moyenne", icon: <Star size={22} /> },
  { value: "<1h", label: "Temps de réponse", icon: <Clock size={22} /> },
];

const marqueeText =
  "CHEF PRIVÉ · SUR MESURE · ÉVÉNEMENTS · GASTRONOMIE · PARIS · LYON · BORDEAUX · MARSEILLE · NICE · TOULOUSE · ";

// ─── Page ────────────────────────────────────────────────────────────────────

function PreviewPage() {
  return (
    <main className="overflow-hidden">
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .marquee-inner { animation: marquee 22s linear infinite; display: flex; width: max-content; }
      `}</style>

      <HeroSection />
      <MarqueeSection />
      <HowItWorksSection />
      <ChefsSection />
      <ReviewsSection />
      <StatsSection />
      <CtaSection />
    </main>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section className="bg-dark-blue min-h-screen relative flex items-center overflow-hidden px-6 py-24">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[640px] h-[640px] rounded-full bg-primary/5 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-primary/5 translate-y-1/3 -translate-x-1/3 pointer-events-none" />

      <div className="container mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-primary/15 border border-primary/30 rounded-full px-4 py-2 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-primary text-sm font-medium">
                Service disponible partout en France
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-h1 text-white leading-[1.05] mb-6"
            >
              Votre chef{" "}
              <span className="text-primary">privé,</span>
              <br />
              exactement comme
              <br />
              vous l&apos;imaginez.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-p text-white/60 max-w-md mb-10 leading-relaxed"
            >
              Décrivez votre occasion en 2 minutes. Notre équipe sélectionne le
              chef idéal parmi{" "}
              <span className="text-white font-semibold">+150 profils</span> et
              vous contacte{" "}
              <span className="text-white font-semibold">sous 1h</span> avec une
              proposition personnalisée.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <Button
                size="lg"
                className="group bg-primary hover:bg-primary/90 text-white font-button-large py-6 px-8 flex items-center gap-3 shadow-lg shadow-primary/30"
              >
                <Sparkles size={18} />
                Faire une demande
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Button>
              <Link
                to="/trouver-un-chef/chefs"
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors font-medium px-4"
              >
                Voir nos chefs <ChevronRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap gap-5"
            >
              {[
                { icon: <Clock size={14} />, text: "Réponse sous 1h" },
                { icon: <ShieldCheck size={14} />, text: "Sans engagement" },
                { icon: <Star size={14} />, text: "4.9/5 · 234 avis" },
              ].map((b) => (
                <div
                  key={b.text}
                  className="flex items-center gap-1.5 text-white/50 text-sm"
                >
                  <span className="text-primary">{b.icon}</span>
                  {b.text}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: decorative card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative h-[500px] max-lg:hidden"
          >
            {/* Main glass card */}
            <div className="absolute top-8 left-8 right-0 bottom-0 bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
              <div className="p-8 relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                    <UtensilsCrossed size={22} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Sophie Laurent</p>
                    <p className="text-white/50 text-sm">
                      Cuisine française gastronomique
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap mb-6">
                  {["Dîner gastronomique", "12 pers.", "Paris 8ème"].map(
                    (t) => (
                      <span
                        key={t}
                        className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/70"
                      >
                        {t}
                      </span>
                    )
                  )}
                </div>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={14}
                      fill="currentColor"
                      className="text-primary"
                    />
                  ))}
                  <span className="text-white/70 text-sm ml-2">5/5</span>
                </div>
                <p className="text-white/50 text-sm italic">
                  &quot;Une expérience culinaire absolument parfaite.&quot;
                </p>
              </div>
            </div>

            {/* Floating stat */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute top-0 right-0 bg-primary rounded-2xl p-4 shadow-xl"
            >
              <p className="text-white text-3xl font-bold leading-none">150+</p>
              <p className="text-white/80 text-sm mt-1">Chefs disponibles</p>
            </motion.div>

            {/* Floating response */}
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{
                repeat: Infinity,
                duration: 3.5,
                ease: "easeInOut",
                delay: 0.5,
              }}
              className="absolute bottom-0 left-0 bg-white rounded-2xl p-4 shadow-xl"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <p className="text-dark-blue text-sm font-semibold">
                  Réponse garantie
                </p>
              </div>
              <p className="text-gray-400 text-xs">
                en moins d&apos;1 heure ouvrée
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Marquee ─────────────────────────────────────────────────────────────────

function MarqueeSection() {
  return (
    <div className="bg-primary py-3.5 overflow-hidden">
      <div className="flex overflow-hidden">
        <div className="marquee-inner">
          <span className="text-white font-bold text-xs tracking-widest whitespace-nowrap pr-8">
            {marqueeText}
            {marqueeText}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────────

function HowItWorksSection() {
  return (
    <section className="bg-white py-24 px-6 overflow-hidden">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-salmon border border-primary/20 rounded-full px-4 py-1.5 mb-4">
            <Clock size={12} className="text-primary" />
            <span className="text-primary text-xs font-semibold">
              En moins de 2 minutes
            </span>
          </div>
          <h2 className="font-h2 max-w-lg">
            Comment ça{" "}
            <span className="text-primary">fonctionne ?</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-10">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="relative group"
            >
              {/* Giant number */}
              <div className="absolute -top-4 -left-2 text-[110px] font-bold text-gray-50 leading-none select-none pointer-events-none transition-colors duration-300 group-hover:text-salmon">
                {step.num}
              </div>
              <div className="relative z-10 pt-14">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {step.icon}
                </div>
                <h3 className="font-h4 mb-2">{step.title}</h3>
                <p className="font-small text-muted-foreground leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mt-14 flex justify-center"
        >
          <Button
            size="lg"
            className="group bg-dark-blue text-white hover:bg-dark-blue/90 font-button-large py-6 px-10 flex items-center gap-3"
          >
            Faire ma demande maintenant
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Chefs ────────────────────────────────────────────────────────────────────

function ChefsSection() {
  return (
    <section className="bg-salmon py-24 px-6">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 gap-4"
        >
          <div>
            <p className="text-primary font-semibold text-xs uppercase tracking-widest mb-2">
              Nos talents
            </p>
            <h2 className="font-h2">
              Des chefs <span className="text-primary">d&apos;exception</span>
            </h2>
          </div>
          <Link
            to="/trouver-un-chef/chefs"
            className="flex items-center gap-2 text-dark-blue font-semibold hover:text-primary transition-colors text-sm"
          >
            Voir les 150+ chefs <ArrowRight size={16} />
          </Link>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {chefs.map((chef, i) => (
            <motion.div
              key={chef.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{ marginTop: i === 1 ? 24 : i === 3 ? 12 : 0 }}
            >
              <div
                className={`rounded-2xl p-6 flex flex-col gap-4 ${chef.cardClass}`}
              >
                {/* Avatar initial */}
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold ${
                    chef.light
                      ? "bg-white/20 text-white"
                      : "bg-dark-blue/10 text-dark-blue"
                  }`}
                >
                  {chef.name.charAt(0)}
                </div>

                {/* Tag */}
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full w-fit ${
                    chef.light
                      ? "bg-white/20 text-white"
                      : "bg-dark-blue/10 text-dark-blue"
                  }`}
                >
                  {chef.tag}
                </span>

                {/* Name & specialty */}
                <div>
                  <p
                    className={`font-h4 ${chef.light ? "text-white" : "text-dark-blue"}`}
                  >
                    {chef.name}
                  </p>
                  <p
                    className={`font-small ${chef.light ? "text-white/70" : "text-muted-foreground"}`}
                  >
                    {chef.specialty}
                  </p>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center mt-2">
                  <div className="flex items-center gap-1">
                    <Star
                      size={12}
                      fill="currentColor"
                      className={chef.light ? "text-white" : "text-primary"}
                    />
                    <span
                      className={`text-sm font-semibold ${chef.light ? "text-white" : "text-dark-blue"}`}
                    >
                      {chef.rating}
                    </span>
                  </div>
                  <div
                    className={`flex items-center gap-1 text-xs ${chef.light ? "text-white/60" : "text-muted-foreground"}`}
                  >
                    <MapPin size={12} />
                    {chef.location}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Reviews ──────────────────────────────────────────────────────────────────

function ReviewsSection() {
  return (
    <section className="bg-dark-blue py-24 px-6">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={18}
                fill="currentColor"
                className="text-primary"
              />
            ))}
          </div>
          <h2 className="font-h2 text-white mb-3">
            Ils nous ont fait{" "}
            <span className="text-primary">confiance</span>
          </h2>
          <p className="font-p text-white/50">
            +234 avis · Note moyenne 4.9/5
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {reviews.map((review, i) => (
            <motion.div
              key={review.author}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              style={{ marginTop: i === 1 ? 32 : 0 }}
            >
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4 hover:bg-white/[0.08] transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Quote size={16} className="text-primary" />
                </div>
                <p className="font-small text-white/70 leading-relaxed italic flex-1">
                  &quot;{review.text}&quot;
                </p>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={12}
                      fill="currentColor"
                      className="text-primary"
                    />
                  ))}
                </div>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white font-semibold text-sm">
                    {review.author}
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">{review.event}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Stats ────────────────────────────────────────────────────────────────────

function StatsSection() {
  return (
    <section className="bg-white py-20 px-6 border-y border-border">
      <div className="container mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex flex-col items-center text-center gap-3"
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                {stat.icon}
              </div>
              <p className="font-h2 text-dark-blue leading-none">{stat.value}</p>
              <p className="font-small text-muted-foreground">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA ─────────────────────────────────────────────────────────────────────

function CtaSection() {
  return (
    <section className="bg-primary py-24 px-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 translate-x-1/3 -translate-y-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white/5 -translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="container mx-auto relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-white/70 font-semibold text-xs uppercase tracking-widest mb-4">
            Prêt à vous lancer ?
          </p>
          <h2 className="font-h2 text-white mb-6 max-w-2xl mx-auto">
            Votre prochaine expérience culinaire commence ici.
          </h2>
          <p className="font-p text-white/70 mb-10 max-w-md mx-auto">
            Aucun engagement. Réponse garantie en moins d&apos;1 heure ouvrée.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 font-button-large py-6 px-10 group flex items-center gap-3 shadow-xl"
            >
              <Sparkles size={18} />
              Faire une demande gratuite
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </Button>
            <Link
              to="/trouver-un-chef/chefs"
              className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors font-medium px-6"
            >
              Parcourir les chefs <ChevronRight size={16} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
